// Serverdaten in Komponenten: zwei Hooks zum Lesen, einer zum Ändern – alle über Vertragsobjekte typisiert.
//
//   const items = useActionQuery(itemGet, { id })                         // lesen
//   const list  = useInfiniteActionQuery(itemList, { q, sort, groupId })  // Liste, per Keyset geblättert
//   const save  = useActionMutation(itemUpdate, { successMessage: 'Gespeichert' })
//   save.mutate({ id, name })
//
// Festschreibende Aktionen (risk 'destructive' | 'financial') gehören hinter useConfirmedAction (./confirm).
import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type SkipToken,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useCallback, useMemo, useRef } from 'react'
import type { AnyActionContract, InputArg, OutputOf } from '@shared/contract'
import { toAppError, type AppError } from '@shared/errors'
import { newIdempotencyKey } from '@shared/ids'
import { toast } from '../ui/Toast'
import { isReauthRequired } from './auth/errors'
import { requestReauth } from './auth/reauthStore'
import { isUnreachableError, type ApiClient } from './client'
import { useApiClient } from './context'
import { toastError } from './errors'
import { ACTION_KEY, actionQueryKey, invalidationMatches, type InvalidateTarget, type RefreshRule } from './keys'
import type { ActionMeta, ActionOutcome } from './types'

export { skipToken }

/** Listen-Vertrag nach '@shared/list': Ausgabe { rows, nextCursor, total? }. */
export function isListContract(contract: AnyActionContract): boolean {
  const shape = (contract.output as unknown as { shape?: Record<string, unknown> }).shape
  return typeof shape === 'object' && shape !== null && 'rows' in shape && 'nextCursor' in shape
}

/** Ohne undefined-Einträge: Ein ausdrückliches undefined würde die Voreinstellungen des Abfrage-Caches überschreiben. */
function defined<T extends object>(options: T): T {
  return Object.fromEntries(Object.entries(options).filter(([, value]) => value !== undefined)) as T
}

function assertReadContract(contract: AnyActionContract, hook: string): void {
  if (contract.risk !== 'read') {
    throw new Error(
      `[@api] ${hook}(${contract.name}): Die Aktion ändert Daten (risk '${contract.risk}') – dafür ist useActionMutation bzw. useConfirmedAction da.`,
    )
  }
}

// ---- lesen ------------------------------------------------------------------------------------------------

export interface ActionQueryOptions<TOutput, TData = TOutput> {
  enabled?: boolean
  /** Ausschnitt oder Umformung; die Komponente zeichnet nur neu, wenn sich der Ausschnitt ändert */
  select?: (data: TOutput) => TData
  /** ms, in denen die Daten als frisch gelten (Standard 15 s) */
  staleTime?: number
  gcTime?: number
  /** regelmäßig neu laden (Zähler, die von fremden Ereignissen abhängen) */
  refetchInterval?: number | false
  refetchOnWindowFocus?: boolean
  /** bisherige Daten stehen lassen, bis die neuen da sind – Standard: an bei Listen */
  keepPrevious?: boolean
  /** welche Echtzeit-Hinweise neu laden – Standard 'module' (siehe ./keys) */
  refreshOn?: RefreshRule
}

/**
 * Lesende Aktion als Abfrage. Schlüssel = Aktion + Eingabe; gleiche Eingabe an zwei Stellen teilt sich
 * Anfrage und Cache. `skipToken` statt der Eingabe schaltet die Abfrage ab (z. B. solange keine ID gewählt ist).
 */
export function useActionQuery<C extends AnyActionContract, TData = OutputOf<C>>(
  contract: C,
  input: InputArg<C> | SkipToken,
  options: ActionQueryOptions<OutputOf<C>, TData> = {},
): UseQueryResult<TData, AppError> {
  assertReadContract(contract, 'useActionQuery')
  const client = useApiClient()
  const keepPrevious = options.keepPrevious ?? isListContract(contract)
  return useQuery<OutputOf<C>, AppError, TData>({
    queryKey: actionQueryKey(contract, input === skipToken ? null : input),
    queryFn:
      input === skipToken
        ? skipToken
        : ({ signal }) =>
            client.run(contract, input, { signal }).catch((cause: unknown) => Promise.reject(toAppError(cause))),
    ...defined({
      enabled: options.enabled,
      select: options.select,
      staleTime: options.staleTime,
      gcTime: options.gcTime,
      refetchInterval: options.refetchInterval,
      refetchOnWindowFocus: options.refetchOnWindowFocus,
      placeholderData: keepPrevious ? (keepPreviousData as never) : undefined,
    }),
    meta: { refreshOn: options.refreshOn ?? 'module' },
  })
}

export type ListRowOf<C extends AnyActionContract> = OutputOf<C> extends { rows: readonly (infer R)[] } ? R : never

export interface InfiniteActionQueryOptions {
  enabled?: boolean
  staleTime?: number
  /** Standard an: beim Wechsel von Filter oder Sortierung bleiben die bisherigen Zeilen stehen */
  keepPrevious?: boolean
  refreshOn?: RefreshRule
}

export interface InfiniteActionQuery<C extends AnyActionContract> {
  /** alle bisher geladenen Zeilen */
  rows: ListRowOf<C>[]
  /** Gesamtzahl, wenn die Aktion sie liefert */
  total: number | undefined
  /** die geladenen Seiten (für Zusatzfelder der Ausgabe, z. B. unreadCount) */
  pages: OutputOf<C>[]
  hasMore: boolean
  loadMore: () => void
  /** Zustand in der Schreibweise der DataTable */
  status: 'loading' | 'ready' | 'refreshing' | 'error'
  isPending: boolean
  isFetching: boolean
  isFetchingMore: boolean
  /** zeigt gerade die Zeilen der vorigen Filter (neue sind unterwegs) */
  isPlaceholder: boolean
  error: AppError | null
  refetch: () => void
}

/**
 * Liste nach '@shared/list', per Keyset geblättert: `page.cursor` setzt der Hook selbst (nextCursor der vorigen
 * Seite). Die Eingabe trägt Suche, Sortierung, Filter und höchstens `page.limit`.
 */
export function useInfiniteActionQuery<C extends AnyActionContract>(
  contract: C,
  input: InputArg<C> | SkipToken,
  options: InfiniteActionQueryOptions = {},
): InfiniteActionQuery<C> {
  assertReadContract(contract, 'useInfiniteActionQuery')
  const client = useApiClient()
  type Page = OutputOf<C> & { rows: ListRowOf<C>[]; nextCursor: string | null; total?: number }
  const query = useInfiniteQuery<Page, AppError, InfiniteData<Page, string | null>, readonly unknown[], string | null>({
    queryKey: [...actionQueryKey(contract, input === skipToken ? null : input), 'infinite'],
    queryFn:
      input === skipToken
        ? skipToken
        : ({ pageParam, signal }) => {
            const base = (input ?? {}) as { page?: { limit?: number } }
            const paged = { ...base, page: { ...base.page, cursor: pageParam } } as InputArg<C>
            return client
              .run(contract, paged, { signal })
              .catch((cause: unknown) => Promise.reject(toAppError(cause))) as Promise<Page>
          },
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    ...defined({
      enabled: options.enabled,
      staleTime: options.staleTime,
      placeholderData: (options.keepPrevious ?? true) ? (keepPreviousData as never) : undefined,
    }),
    meta: { refreshOn: options.refreshOn ?? 'module' },
  })
  const pages = query.data?.pages
  const rows = useMemo(() => (pages ?? []).flatMap((page) => page.rows), [pages])
  const { fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = query
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])
  return {
    rows,
    total: pages?.[0]?.total,
    pages: (pages ?? []) as OutputOf<C>[],
    hasMore: Boolean(hasNextPage),
    loadMore,
    status: query.isPending
      ? 'loading'
      : query.isError && rows.length === 0
        ? 'error'
        : (query.isFetching && !isFetchingNextPage) || query.isPlaceholderData
          ? 'refreshing'
          : 'ready',
    isPending: query.isPending,
    isFetching: query.isFetching,
    isFetchingMore: isFetchingNextPage,
    isPlaceholder: query.isPlaceholderData,
    error: query.error,
    refetch: () => void refetch(),
  }
}

// ---- ändern -----------------------------------------------------------------------------------------------

/** Abfragen neu laden, die von der ausgeführten Aktion betroffen sind. */
export function invalidateAfter(
  queryClient: QueryClient,
  contract: AnyActionContract,
  target: InvalidateTarget | undefined,
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const [scope, name] = query.queryKey
      return scope === ACTION_KEY && typeof name === 'string' && invalidationMatches(target, contract.name, name)
    },
  })
}

export interface ActionMutationOptions<C extends AnyActionContract> {
  /**
   * Abfragen, die danach neu laden. Standard 'module': alle Abfragen des eigenen Moduls. Sonst eine Liste aus
   * Verträgen, Aktionsnamen oder Präfixen (`[itemList, 'stock']`) – oder false.
   */
  invalidate?: InvalidateTarget | ((output: OutputOf<C>, input: InputArg<C>) => InvalidateTarget)
  /** Toast nach Erfolg: „Gespeichert“, „Umlagerung UM-2026-0875 angelegt“. Bietet der Server „Rückgängig“ an, steht es dabei. */
  successMessage?: string | ((output: OutputOf<C>, input: InputArg<C>) => string | null | undefined)
  /** false: keinen Fehler-Toast zeigen (die Seite zeigt den Fehler selbst, z. B. am Formularfeld) */
  errorToast?: boolean | ((error: AppError) => boolean)
  onSuccess?: (output: OutputOf<C>, input: InputArg<C>, meta: ActionMeta | null) => void
  onError?: (error: AppError, input: InputArg<C>) => void
  /** Wartezeit vor der ersten selbsttätigen Wiederholung in ms (Standard 300) */
  retryDelayMs?: number
}

export interface MutateOptions {
  /** effectHash der bestätigten Vorschau (useConfirmedAction setzt ihn selbst) */
  expectedEffectHash?: string
}

export interface ActionMutation<C extends AnyActionContract> {
  /** absenden; Fehler landen im Toast und in `error` */
  mutate: (input: InputArg<C>, options?: MutateOptions) => void
  /** absenden und auf das Ergebnis warten; lehnt mit AppError ab */
  mutateAsync: (input: InputArg<C>, options?: MutateOptions) => Promise<OutputOf<C>>
  /** das letzte Absenden unverändert wiederholen – mit demselben Idempotenzschlüssel */
  retry: () => Promise<OutputOf<C>> | undefined
  isPending: boolean
  isError: boolean
  isSuccess: boolean
  error: AppError | null
  data: OutputOf<C> | undefined
  /** angelegte Datensätze, Rückgängig-Angebot, Wiederholung */
  meta: ActionMeta | null
  reset: () => void
}

interface Variables<I> {
  input: I
  /** ein Schlüssel je Absenden; jede Wiederholung desselben Absendens trägt denselben */
  idempotencyKey: string
  expectedEffectHash?: string
}

/** Rückgängig-Angebot des Servers ausführen (Gegenaktion mit fertiger Eingabe). */
async function runUndo(client: ApiClient, queryClient: QueryClient, contract: AnyActionContract, meta: ActionMeta) {
  if (!meta.undo) return
  await client.runByName(meta.undo.action, meta.undo.input, { idempotencyKey: newIdempotencyKey() })
  await Promise.all([
    invalidateAfter(queryClient, contract, 'module'),
    queryClient.invalidateQueries({ queryKey: actionQueryKey(meta.undo.action) }),
  ])
}

/**
 * Ändernde Aktion. Jedes Absenden bekommt einen Idempotenzschlüssel; scheitert es an der Verbindung oder an einer
 * kurzen Sperre, wird es bis zu zweimal mit demselben Schlüssel wiederholt – doppelt gebucht wird nie.
 * Fehler erscheinen als bleibender Toast mit dem deutschen Satz des Servers.
 */
export function useActionMutation<C extends AnyActionContract>(
  contract: C,
  options: ActionMutationOptions<C> = {},
): ActionMutation<C> {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const optionsRef = useRef(options)
  optionsRef.current = options
  const last = useRef<Variables<InputArg<C>> | null>(null)

  const mutation = useMutation<ActionOutcome<OutputOf<C>>, AppError, Variables<InputArg<C>>>({
    mutationKey: [ACTION_KEY, contract.name],
    mutationFn: async (variables) => {
      const send = () =>
        client
          .runDetailed(contract, variables.input, {
            idempotencyKey: variables.idempotencyKey,
            expectedEffectHash: variables.expectedEffectHash,
          })
          .catch((cause: unknown) => Promise.reject(toAppError(cause)))
      try {
        return await send()
      } catch (failure) {
        // sicherheitsrelevant ohne frische Bestätigung dieser Sitzung: einmal nachfragen (Kennwort oder Code) und
        // denselben Aufruf wiederholen – der Server merkt den Schlüssel erst nach dieser Prüfung (Dispatcher 7b vor 8)
        if (isReauthRequired(failure) && (await requestReauth((failure as AppError).message))) return send()
        throw failure
      }
    },
    retry: (failureCount, error) => failureCount < 2 && (error.retryable || isUnreachableError(error)),
    retryDelay: (attempt) => (optionsRef.current.retryDelayMs ?? 300) * 2 ** attempt,
    onSuccess: (outcome, variables) => {
      const current = optionsRef.current
      const target =
        typeof current.invalidate === 'function'
          ? current.invalidate(outcome.output, variables.input)
          : current.invalidate
      void invalidateAfter(queryClient, contract, target)
      const message =
        typeof current.successMessage === 'function'
          ? current.successMessage(outcome.output, variables.input)
          : current.successMessage
      if (message) {
        const meta = outcome.meta
        toast.success(message, {
          undo: meta?.undo ? () => runUndo(client, queryClient, contract, meta) : undefined,
        })
      }
      current.onSuccess?.(outcome.output, variables.input, outcome.meta)
    },
    onError: (error, variables) => {
      const current = optionsRef.current
      const show = typeof current.errorToast === 'function' ? current.errorToast(error) : (current.errorToast ?? true)
      if (show)
        toastError(error, { contract, retry: () => void mutation.mutateAsync(variables).catch(() => undefined) })
      current.onError?.(error, variables.input)
    },
  })

  const { mutateAsync: send, reset } = mutation
  const mutateAsync = useCallback(
    async (input: InputArg<C>, mutateOptions?: MutateOptions): Promise<OutputOf<C>> => {
      const variables: Variables<InputArg<C>> = {
        input,
        idempotencyKey: newIdempotencyKey(),
        expectedEffectHash: mutateOptions?.expectedEffectHash,
      }
      last.current = variables
      return (await send(variables)).output
    },
    [send],
  )
  const mutate = useCallback(
    (input: InputArg<C>, mutateOptions?: MutateOptions): void => {
      // Fehler sind über onError (Toast) und `error` sichtbar – hier darf nichts unbehandelt bleiben
      void mutateAsync(input, mutateOptions).catch(() => undefined)
    },
    [mutateAsync],
  )
  const retry = useCallback((): Promise<OutputOf<C>> | undefined => {
    const variables = last.current
    return variables ? send(variables).then((outcome) => outcome.output) : undefined
  }, [send])

  return {
    mutate,
    mutateAsync,
    retry,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    data: mutation.data?.output,
    meta: mutation.data?.meta ?? null,
    reset,
  }
}
